// src/Payroll_Management/PayRoll_Report/EmployeeSalarySlipPrint.jsx

import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Printer, X } from "lucide-react";

const BASE_URL = "https://nissi-india.com";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const safeStr = (v) => ((v ?? "") + "").trim();
const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const fmt2 = (v) =>
  safeNum(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function numberToWordsIN(num) {
  num = Math.floor(safeNum(num));
  if (num === 0) return "Zero";

  const a = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
    "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen",
  ];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const two = (n) => (n < 20 ? a[n] : b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : ""));
  const three = (n) => (Math.floor(n / 100) ? a[Math.floor(n / 100)] + " Hundred " : "") + two(n % 100);

  const parts = [];
  const crore = Math.floor(num / 10000000); num %= 10000000;
  const lakh = Math.floor(num / 100000); num %= 100000;
  const thousand = Math.floor(num / 1000); num %= 1000;

  if (crore) parts.push(three(crore) + " Crore");
  if (lakh) parts.push(three(lakh) + " Lakh");
  if (thousand) parts.push(three(thousand) + " Thousand");
  if (num) parts.push(three(num));

  return parts.join(" ").trim();
}

const SlipBlock = ({ row, monthName, year }) => {
  const netPay = safeNum(row?.fld_Netpay);
  const words = `${numberToWordsIN(netPay)} Rupees Only`;

  return (
    <div className="slip border border-black mb-6 break-after-page">
      {/* ===== STATIC HEADER ===== */}
      <div className="grid grid-cols-[180px_1fr] border-b border-black">
        <div className="p-2 border-r border-black flex items-center justify-center">
          <img
            src="/logo.png"
            alt="logo"
            style={{ width: 140, height: 70, objectFit: "contain" }}
          />
        </div>
        <div className="p-2 text-center">
          <div className="text-xl font-extrabold">Nissi Infotech</div>
          <div className="font-semibold">CIVIL CONSTRUCTION</div>
          <div className="text-xs mt-1">
            3/42-12, Nagappa colony, kavundampalayam - Tamil Nadu
          </div>
          <div className="text-xs">Phone No: 9003744644</div>
        </div>
      </div>

      <div className="text-center font-extrabold py-2 border-b border-black">
        SALARY SLIP
      </div>

      <div className="text-center font-bold text-xs py-2 border-b border-black">
        Pay Slip for Month of {monthName} - {year}
      </div>

      {/* ===== SIMPLE DETAILS (like ASP print all screenshot) ===== */}
      <table className="w-full border-collapse text-[11px]">
        <tbody>
          {[
            ["Employee Name", row.fld_employee, "PAN No", row.fld_PANNo],
            ["Designation", row.fld_designationname, "Bank Name", row.fld_bankname],
            ["Gender", row.fld_gender, "Bank A/C No", row.fld_accountno],
            ["Date of Birth", row.DOB, "PF No", row.fld_PFNo],
            ["Department", row.fld_departmentname, "ESI No", row.fld_ESINo],
          ].map((r, i) => (
            <tr key={i} className="border-t border-black">
              <td className="border-r border-black p-2 font-bold w-[160px]">{r[0]}</td>
              <td className="border-r border-black p-2 w-[220px]">{safeStr(r[1])}</td>
              <td className="border-r border-black p-2 font-bold w-[160px]">{r[2]}</td>
              <td className="p-2">{safeStr(r[3])}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ===== Earnings / Deductions ===== */}
      <table className="w-full border-collapse text-[11px] mt-3">
        <thead>
          <tr className="border-t border-black">
            <th className="border-r border-black p-2 text-left font-extrabold">EARNINGS</th>
            <th className="border-r border-black p-2 text-right font-extrabold w-[140px]">AMOUNT</th>
            <th className="border-r border-black p-2 text-left font-extrabold">DEDUCTIONS</th>
            <th className="p-2 text-right font-extrabold w-[140px]">AMOUNT</th>
          </tr>
        </thead>
        <tbody>
          {[
            ["Basic Pay", row.fld_basicpay, "Employee PF", row.fld_pf],
            ["HRA", row.fld_hra, "ESI", row.fld_esi],
            ["Paid Basic", row.fld_paidbasic, "TDS", row.fld_tds],
            ["Conveyance", row.fld_conveyance, "Advance", row.fld_advance],
            ["Educational", row.fld_educational, "Loan Detection", row.loandetection],
            ["Gross Salary", row.fld_GrossSalary, "LOP Amount", row.fld_lopamt],
            ["OT Amount", row.fld_otamt, "", ""],
          ].map((x, i) => (
            <tr key={i} className="border-t border-black">
              <td className="border-r border-black p-2 font-bold">{x[0]}</td>
              <td className="border-r border-black p-2 text-right">{fmt2(x[1])}</td>
              <td className="border-r border-black p-2 font-bold">{x[2]}</td>
              <td className="p-2 text-right">{x[2] ? fmt2(x[3]) : ""}</td>
            </tr>
          ))}

          <tr className="border-t border-black">
            <td className="border-r border-black p-2" />
            <td className="border-r border-black p-2" />
            <td className="border-r border-black p-2 font-extrabold">Total Deductions</td>
            <td className="p-2 text-right font-extrabold">
              {fmt2(
                safeNum(row.fld_pf) +
                safeNum(row.fld_esi) +
                safeNum(row.fld_tds) +
                safeNum(row.fld_advance) +
                safeNum(row.loandetection) +
                safeNum(row.fld_lopamt)
              )}
            </td>
          </tr>

          <tr className="border-t border-black">
            <td colSpan={2} className="border-r border-black p-2 font-bold">
              Rs. {words}
            </td>
            <td className="border-r border-black p-2 font-extrabold">Net Pay</td>
            <td className="p-2 text-right font-extrabold">{fmt2(netPay)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const EmployeeSalarySlipPrint = () => {
  const q = useQuery();

  const finYearId =
    q.get("finYearId") ||
    localStorage.getItem("finYearId") ||
    "";

  const branchId = q.get("branchId") || "";
  const year = q.get("year") || "";
  const monthName = q.get("month") || q.get("monthName") || "";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    if (!finYearId || !branchId || !year || !monthName) {
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${BASE_URL}/api/employee-salary-slip/all`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          year: Number(year),
          monthName: monthName,
        },
      });

      if (res?.data?.success) setRows(res.data.rows || []);
      else setRows([]);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="bg-gray-100 min-h-screen p-4">
      <div className="no-print max-w-4xl mx-auto flex justify-end gap-3 mb-4">
        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 shadow"
        >
          <Printer size={16} />
          Print
        </button>
        <button
          onClick={() => window.close()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-700 text-white font-bold hover:bg-gray-800 shadow"
        >
          <X size={16} />
          Close
        </button>
      </div>

      <div className="sheet max-w-4xl mx-auto bg-white border border-black p-3">
        {loading ? (
          <div className="text-center py-10">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-10">No Records Found</div>
        ) : (
          rows.map((r, idx) => (
            <SlipBlock key={r.fld_employeeid || idx} row={r} monthName={monthName} year={year} />
          ))
        )}
      </div>

      <style>{`
        @page { size: A4 portrait; margin: 10mm; }
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .sheet { border: none !important; padding: 0 !important; }
          .break-after-page { page-break-after: always; }
          .break-after-page:last-child { page-break-after: auto; }
        }
      `}</style>
    </div>
  );
};

export default EmployeeSalarySlipPrint;